
import { Module } from '../types';

export const PERFORMANCE_BASICS_MODULE: Module = {
  id: "intermediate-performance",
  title: "React Performance Basics",
  description: "Learn essential techniques to optimize React application performance",
  duration: "90 minutes",
  difficulty: "intermediate",
  content: `
# React Performance Basics

## Understanding React Performance
React performance optimization involves reducing unnecessary re-renders, optimizing bundle size, and improving user experience metrics.

## Key Performance Concepts
- Virtual DOM reconciliation
- Component re-rendering cycles
- JavaScript bundle optimization
- Runtime performance metrics

## React.memo for Component Optimization
\`\`\`jsx
import React, { memo } from 'react';

const ExpensiveComponent = memo(({ data, onAction }) => {
  return (
    <div>
      {data.map(item => (
        <div key={item.id}>{item.name}</div>
      ))}
    </div>
  );
});
\`\`\`

## useMemo and useCallback Hooks
\`\`\`jsx
import React, { useMemo, useCallback } from 'react';

function DataProcessor({ items, filter }) {
  const expensiveValue = useMemo(() => {
    return items.filter(filter).sort((a, b) => a.priority - b.priority);
  }, [items, filter]);

  const handleClick = useCallback((id) => {
    // Handle click logic
  }, []);

  return (
    <div>
      {expensiveValue.map(item => (
        <Item key={item.id} data={item} onClick={handleClick} />
      ))}
    </div>
  );
}
\`\`\`

## Code Splitting with React.lazy
\`\`\`jsx
import React, { Suspense, lazy } from 'react';

const LazyComponent = lazy(() => import('./LazyComponent'));

function App() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LazyComponent />
    </Suspense>
  );
}
\`\`\`

## Performance Monitoring
- Use React DevTools Profiler
- Monitor Core Web Vitals
- Measure component render times
- Track memory usage patterns
  `,
  codeExamples: [
    {
      title: "Optimized List Component",
      code: `import React, { memo, useMemo } from 'react';

const ListItem = memo(({ item, onSelect }) => (
  <div onClick={() => onSelect(item.id)}>
    {item.name} - {item.category}
  </div>
));

function OptimizedList({ items, selectedCategory, onSelect }) {
  const filteredItems = useMemo(() => {
    return items.filter(item => 
      !selectedCategory || item.category === selectedCategory
    );
  }, [items, selectedCategory]);

  return (
    <div>
      {filteredItems.map(item => (
        <ListItem 
          key={item.id} 
          item={item} 
          onSelect={onSelect} 
        />
      ))}
    </div>
  );
}`,
      explanation: "Optimized list using React.memo and useMemo to prevent unnecessary re-renders",
      language: "jsx"
    }
  ],
  practice: {
    task: "Optimize a slow-rendering product grid component",
    starter: `import React, { useState } from 'react';

function ProductGrid({ products }) {
  const [sortBy, setSortBy] = useState('name');
  
  // This component re-renders too often and is slow
  // TODO: Optimize using React performance techniques
  
  const sortedProducts = products.sort((a, b) => {
    return a[sortBy].localeCompare(b[sortBy]);
  });

  return (
    <div>
      <select onChange={(e) => setSortBy(e.target.value)}>
        <option value="name">Name</option>
        <option value="price">Price</option>
      </select>
      {sortedProducts.map(product => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
}`,
    solution: `import React, { useState, useMemo, memo } from 'react';

const ProductCard = memo(({ product }) => (
  <div className="product-card">
    <h3>{product.name}</h3>
    <p>\${product.price}</p>
  </div>
));

function ProductGrid({ products }) {
  const [sortBy, setSortBy] = useState('name');
  
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      if (sortBy === 'price') {
        return a.price - b.price;
      }
      return a[sortBy].localeCompare(b[sortBy]);
    });
  }, [products, sortBy]);

  return (
    <div>
      <select onChange={(e) => setSortBy(e.target.value)}>
        <option value="name">Name</option>
        <option value="price">Price</option>
      </select>
      {sortedProducts.map(product => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
}`,
    hints: [
      "Use useMemo for expensive calculations",
      "Wrap child components in React.memo",
      "Avoid mutating props directly",
      "Consider the dependency array carefully"
    ]
  },
  quickCheck: {
    questions: [
      {
        id: "perf-q1",
        question: "When should you use React.memo?",
        options: [
          "For all components",
          "For components that re-render frequently with the same props",
          "Only for class components",
          "Never, it's deprecated"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "medium"
      },
      {
        id: "perf-q2",
        question: "What does useMemo help optimize?",
        options: [
          "Network requests",
          "Expensive calculations between re-renders",
          "Component mounting",
          "CSS rendering"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "easy"
      },
      {
        id: "perf-q3",
        question: "What is the main benefit of code splitting?",
        options: [
          "Faster development",
          "Smaller initial bundle size and faster loading",
          "Better debugging",
          "Easier testing"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "hard"
      }
    ]
  }
};
